/**
 * Collections — a shop's own named groups of products (Petal_Some Ug.,
 * 27 Sep 2026: "Timeless Valentine Bouquets", "Vals Edition 2", "The Red
 * Spot"). Unlike categories and product types, which every shop on the
 * platform shares, a collection belongs to one shop: its own name, a cover,
 * its own order, and optionally a season it shows in.
 *
 * Pure helpers shared by the POS and the website. Seasons are days of the
 * year written MMDD (201 = 1 Feb, 1214 = 14 Dec) and repeat every year; a
 * season may run over New Year (1215 → 105).
 *
 * WHEN a season is on is decided in the visitor's browser with their own
 * clock: a Convex query that read the time would never cache, and Convex runs
 * in UTC, so the season would open at the wrong hour in Kampala.
 */

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** MMDD for a date, in the local time of whoever calls it. */
export function monthDay(date: Date): number {
  return (date.getMonth() + 1) * 100 + date.getDate();
}

/** True when `md` (MMDD) is a real day of the year. */
export function isMonthDay(md: unknown): md is number {
  if (typeof md !== "number" || !Number.isInteger(md)) return false;
  const month = Math.floor(md / 100);
  const day = md % 100;
  return month >= 1 && month <= 12 && day >= 1 && day <= DAYS_IN_MONTH[month - 1];
}

/**
 * Is a collection in season on `date`? No season (either end missing) = all
 * year. A season may wrap over New Year.
 */
export function inSeason(from: number | undefined | null, to: number | undefined | null, date: Date = new Date()): boolean {
  return inSeasonOn(from, to, monthDay(date));
}

/** inSeason for a day already written MMDD (the browser sends its own to a query). */
export function inSeasonOn(from: number | undefined | null, to: number | undefined | null, md: number): boolean {
  if (!isMonthDay(from) || !isMonthDay(to)) return true;
  return from <= to ? md >= from && md <= to : md >= from || md <= to;
}

/** "14 Feb" */
export function monthDayLabel(md: number): string {
  if (!isMonthDay(md)) return "";
  return `${md % 100} ${MONTHS[Math.floor(md / 100) - 1]}`;
}

/** "1–14 Feb", "15 Dec – 5 Jan", "All year". */
export function seasonLabel(from: number | undefined | null, to: number | undefined | null): string {
  if (!isMonthDay(from) || !isMonthDay(to)) return "All year";
  const fm = Math.floor(from / 100);
  const tm = Math.floor(to / 100);
  if (fm === tm && from <= to) return `${from % 100}–${to % 100} ${MONTHS[fm - 1]}`;
  return `${monthDayLabel(from)} – ${monthDayLabel(to)}`;
}

/** A URL-safe slug from a collection name: "Timeless Valentine Bouquets" → "timeless-valentine-bouquets". */
export function collectionSlug(name: string): string {
  const base = String(name ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
  return base || "collection";
}

/** Longest a collection name may be. */
export const COLLECTION_NAME_MAX = 60;
/** Most products one collection holds (a page of cards, not a catalogue). */
export const COLLECTION_PRODUCT_CAP = 300;
